import React from 'react';
import {CircleFlag} from 'react-circle-flags';
import supportedCountries from '../../i18n/supported-countries.json';
import taxCodesByCountry from '../../i18n/tax_codes_by_country.json';
import {objectHelpers} from './object.helpers';


/**
 * Collection of helper functions for working with countries, flags, and localization data.
 */
export const localizationHelpers = {
  /**
   * Returns the full list of supported countries sorted alphabetically by name.
   * @returns {Array<Object>} Sorted array of country objects from the supported-countries data.
   * @example
   * localizationHelpers.getSupportedCountries(); // [{ name: 'Albania', 'ISO3166-1-Alpha-2': 'AL', ... }, ...]
   */
  getSupportedCountries: () => supportedCountries.sort((a, b) => a.name.localeCompare(b.name)),
  /**
   * Finds a country object by its ISO 3166-1 Alpha-2 code, with an optional fallback to a default country.
   * @param {string} code - ISO 3166-1 Alpha-2 country code to search for (case-insensitive).
   * @param {boolean} [useDefault=false] - When true and no match is found, returns the default country instead.
   * @param {string} [defaultKey='it'] - ISO 3166-1 Alpha-2 code of the fallback country.
   * @returns {Object|null} Matching country object, or null if not found and useDefault is false.
   * @example
   * localizationHelpers.getCountryByCode('FR'); // { name: 'France', 'ISO3166-1-Alpha-2': 'FR', ... }
   */
  getCountryByCode: (code, useDefault = false, defaultKey = 'it') => {
    let country = null;
    let activeCountry = supportedCountries.filter(c => c['ISO3166-1-Alpha-2'].toLowerCase() === code.toLowerCase());
    if (activeCountry.length > 0) {
      country = activeCountry[0]
    } else {
      if (useDefault) {
        let activeCountry = supportedCountries.filter(c => c['ISO3166-1-Alpha-2'].toLowerCase() === defaultKey.toLowerCase());
        if (activeCountry.length > 0) country = activeCountry[0];
      }
    }
    return country;
  },
  /**
   * Looks up the ISO 3166-1 Alpha-2 code for a country given its English name.
   * @param {string} name - Country name to search for (case-insensitive).
   * @returns {string} Lowercase Alpha-2 code, or an empty string if no match is found.
   * @example
   * localizationHelpers.getCountryCodeByName('France'); // 'fr'
   */
  getCountryCodeByName: (name) => {
    if (typeof name === 'string') name = name.toLowerCase();
    let activeCountry = supportedCountries.find(c => c.name.toLowerCase() === name);
    return activeCountry ? activeCountry['ISO3166-1-Alpha-2'].toLowerCase() : '';
  },
  /**
   * Returns a flag element for the country matching the given name.
   *
   * Renders through `react-circle-flags`, the same CDN-backed renderer `getCountryOptions` below
   * already uses. It replaced `react-world-flags`, which inlined an SVG for every flag in the world
   * into the entry bundle — 1.19 MB gzipped, 23% of everything a user downloads before the first
   * screen, to draw the two flags on the Vendors page (H1MVP-4740).
   *
   * Takes a height rather than a style object: a circle flag is round and square, so `borderRadius`
   * is a no-op on it and a `width` that disagrees with the height only distorts it.
   *
   * An unknown or empty code renders the library's `xx` placeholder rather than a broken image.
   *
   * @param {string} name - Country name used to resolve the Alpha-2 code.
   * @param {{height?: number}} [options={}] - Rendered size in pixels.
   * @returns {React.ReactElement} Flag image element.
   * @example
   * localizationHelpers.getCountryFlagByName('France'); // <CircleFlag countryCode="fr" height={16} />
   */
  getCountryFlagByName: (name, {height = 16} = {}) => (
    <CircleFlag
      countryCode={localizationHelpers.getCountryCodeByName(name)}
      height={height}
    />
  ),
  /**
   * Returns the English name for a country given its ISO 3166-1 Alpha-2 code, falling back to the code itself.
   * @param {string} countryCode - ISO 3166-1 Alpha-2 country code.
   * @returns {string} Country name, or the original code string if no match is found.
   * @example
   * localizationHelpers.getCountryName('DE'); // 'Germany'
   */
  getCountryName: (countryCode) => {
    let country = localizationHelpers.getCountryByCode(countryCode);
    return country ? country.name : countryCode
  },
  /**
   * Returns a sorted array of select option objects for all supported countries, each with a CircleFlag image.
   * @param {string} [propName='ISO3166-1-Alpha-2'] - Country object property to use as the option value.
   * @returns {Array<{label: string, value: string, image: React.ReactElement}>} Array of country option objects.
   * @example
   * localizationHelpers.getCountryOptions(); // [{ label: 'Albania', value: 'AL', image: <CircleFlag ... /> }, ...]
   */
  getCountryOptions: (propName = 'ISO3166-1-Alpha-2') => {
    const countries = localizationHelpers.getSupportedCountries();
    return countries.map(c => {
      let flagCode = '';
      let value = '';

      try {
        value = c[propName];
        flagCode = c['ISO3166-1-Alpha-2'];
      } catch (e) {
        const alphaThreeCode = c['ISO3166-1-Alpha-3'];
        if (alphaThreeCode) {
          flagCode = value = alphaThreeCode;
        }
      }

      return {
        label: c.name,
        value: value,
        image: (
          <CircleFlag
            countryCode={flagCode.toLowerCase()}
            height={22}
          />
        )
      }
    });
  },
  /**
   * Retrieves the tax code string for a given country from the tax_codes_by_country data.
   * @param {string|null|undefined} countryCode - ISO 3166-1 Alpha-2 or Alpha-3 country code (converted to uppercase).
   * @returns {string} Tax code string for the country, or an empty string if countryCode is falsy.
   * @example
   * localizationHelpers.getTaxCodeByCountry('IT'); // 'IT'
   */
  getTaxCodeByCountry: (countryCode) => countryCode ? objectHelpers.getObjProp(taxCodesByCountry, countryCode.toUpperCase()) : '',
}
